import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Card } from './card';
import { Skeleton } from './skeleton';

/**
 * StatTile — a headline number that is itself the link.
 *
 * The whole tile is the target rather than a "View all →" button tucked
 * underneath it: the number is what the eye goes to, so it should be what the
 * cursor can hit.
 *
 * Pass `to` for a route, or `onClick` for in-page navigation such as switching
 * tab. With neither it renders as a plain, non-interactive tile.
 */
const TONES = {
  default: 'text-muted-foreground',
  positive: 'text-emerald-600 dark:text-emerald-400',
  info: 'text-sky-600 dark:text-sky-400',
  warning: 'text-amber-600 dark:text-amber-400',
  critical: 'text-red-600 dark:text-red-400'
};

function StatTile({ icon: Icon, label, value, hint, tone = 'default', to, onClick, loading = false, className }) {
  const interactive = Boolean(to || onClick);

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {Icon && <Icon className={cn('h-5 w-5 shrink-0', TONES[tone])} />}
      </div>

      {loading ? (
        <Skeleton className="mt-2 h-9 w-20" />
      ) : (
        <p className={cn('mt-1 text-3xl font-bold tabular-nums', tone !== 'default' && TONES[tone])}>{value}</p>
      )}

      <div className="mt-1 flex items-center justify-between gap-2">
        <p className="truncate text-xs text-muted-foreground">{hint}</p>
        {interactive && (
          <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/stat:opacity-100" />
        )}
      </div>
    </>
  );

  const card = (
    <Card
      className={cn(
        'group/stat h-full p-6 text-left transition-shadow',
        interactive && 'cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      {body}
    </Card>
  );

  if (to) {
    return (
      <Link to={to} className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {card}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {card}
      </button>
    );
  }

  return card;
}

export { StatTile };
